import { useQueries } from '@tanstack/react-query';

import { getMovers, getStats, getSummary } from '../api/client';
import type { Stats, Quake, Summary } from '../api/types';
import { filterMovers, type DashboardFilters } from '../lib/filters';

interface DashboardData {
  /** Movers filtered by the user's current mode (confirmed/watchlist), category, query, window. */
  movers: Quake[];
  /** Top watchlist-band movers (heat 45-64), always fetched regardless of current mode.
   *  Used as the "almost confirmed" fallback when the user's current filter is empty,
   *  and as the Featured Market / Correlation fallback. */
  watchlistFallback: Quake[];
  stats: Stats;
  summary: Summary;
  isLoading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
}

export function useDashboardData(filters: DashboardFilters): DashboardData {
  const [summaryQuery, statsQuery, moversQuery, watchlistQuery] = useQueries({
    queries: [
      {
        queryKey: ['summary'],
        queryFn: ({ signal }) => getSummary({ signal }),
        refetchInterval: 2_000,
      },
      {
        queryKey: ['stats'],
        queryFn: ({ signal }) => getStats({ signal }),
        refetchInterval: 5_000,
      },
      {
        queryKey: ['movers', filters],
        queryFn: ({ signal }) => getMovers(filters, { signal, limit: 20 }),
        refetchInterval: 2_000,
      },
      {
        // Always fetch watchlist-band movers in parallel so we can show
        // "almost confirmed" candidates and a featured-market fallback
        // even when the user's current mode (typically Confirmed) is empty.
        queryKey: ['movers', 'watchlist-fallback', { window: filters.window }],
        queryFn: ({ signal }) =>
          getMovers(
            { ...filters, mode: 'watchlist', query: '', category: 'all' },
            { signal, limit: 10 },
          ),
        refetchInterval: 2_000,
      },
    ],
  });

  const summary = summaryQuery.data ?? emptySummary();
  const stats = statsQuery.data ?? emptyStats();
  const rawMovers = moversQuery.data?.items ?? [];
  const movers = filterMovers(rawMovers, filters);
  const watchlistRaw = watchlistQuery.data?.items ?? [];
  // Sort the watchlist fallback by heat descending so the first item is
  // the strongest "almost confirmed" candidate.
  const watchlistFallback = [...watchlistRaw].sort((a, b) => b.score - a.score);
  const error = [
    summaryQuery.error,
    statsQuery.error,
    moversQuery.error,
    watchlistQuery.error,
  ]
    .filter(Boolean)
    .map((err) => (err instanceof Error ? err.message : 'Unknown API error'))[0];

  return {
    movers,
    watchlistFallback,
    stats,
    summary,
    isLoading:
      summaryQuery.isLoading ||
      statsQuery.isLoading ||
      moversQuery.isLoading ||
      watchlistQuery.isLoading,
    error: error ?? null,
    lastUpdatedAt: new Date(),
  };
}

function emptyStats(): Stats {
  return {
    markets: 0,
    assets: 0,
    active_assets: 0,
    resolved_assets: 0,
    messages: 0,
    book_messages: 0,
    price_messages: 0,
    trade_messages: 0,
    best_bid_ask_messages: 0,
    unknown_messages: 0,
    scoring_attempts: 0,
    confirmed: 0,
    watchlist: 0,
    median_heat: 0,
    median_reliability: 0,
  };
}

function emptySummary(): Summary {
  return {
    active_markets: 0,
    active_assets: 0,
    quake_events_today: 0,
    avg_move_5m_pp: 0,
    markets_with_catalyst_match_pct: 0,
    live_streams_connected: 0,
    last_discovery_at: '',
    last_message_at: '',
  };
}